import React from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  GTranslate,
  Visibility,
  Circle,
  Edit,
  Delete,
} from "@mui/icons-material";
import { EditorPhrase } from "../../api/phrasebook_editor";
import { PhraseStatus } from "../../api/phrasebook";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../pages/playground/types";
import { phraseColorByStatus } from "../editor/phrasebook/colors";
import FormattedText from "../common/formatted-text";

interface PhrasebookTableProps {
  phrases: EditorPhrase[];
  loading: boolean;
  brands: Array<{ id: string; name: string }>;
  onBrandFilter: (brandId: string) => void;
  onShowUsage: (phrase: EditorPhrase) => void;
  onEditPhrase: (phrase: EditorPhrase) => void;
  onDeletePhrase: (phrase: EditorPhrase) => void;
}

const PhrasebookTable: React.FC<PhrasebookTableProps> = ({
  phrases,
  loading,
  brands,
  onBrandFilter,
  onShowUsage,
  onEditPhrase,
  onDeletePhrase,
}) => {
  const getBrandName = (brandId: string) => {
    return brands.find((b) => b.id === brandId)?.name || brandId;
  };

  const getStatusLabel = (status: PhraseStatus | undefined) => {
    switch (status) {
      case PhraseStatus.UNREVIEWED:
        return "Unreviewed";
      case PhraseStatus.INTERNALLY_REVIEWED:
        return "Internally Reviewed";
      case PhraseStatus.EXTERNALLY_REVIEWED:
        return "Externally Reviewed";
      default:
        return status || "Unknown";
    }
  };

  const getTranslationRatio = (phrase: EditorPhrase) => {
    if (!phrase.translation_status) return { available: 0, total: 0 };

    const entries = Object.entries(phrase.translation_status);
    const available = entries.filter(([, isAvailable]) => isAvailable).length;
    const total = entries.length;

    return { available, total };
  };

  const getTranslationColor = (phrase: EditorPhrase) => {
    const { available, total } = getTranslationRatio(phrase);
    if (total === 0) return "#e0e0e0";

    const ratio = available / total;
    if (ratio === 1) return "#c8e6c9"; // Light green
    if (ratio >= 0.7) return "#dcedc8"; // Lighter green
    if (ratio >= 0.5) return "#fff9c4"; // Light yellow
    if (ratio > 0) return "#ffe0b2"; // Light orange
    return "#ffcdd2"; // Light red
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Phrase</TableCell>
              <TableCell>Suitability</TableCell>
              <TableCell>Brands</TableCell>
              <TableCell>Translations</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !phrases || phrases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No phrases found
                </TableCell>
              </TableRow>
            ) : (
              phrases?.map((phrase) => (
                <TableRow key={phrase.id} hover>
                  <TableCell sx={{ maxWidth: 400, wordBreak: "break-word" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip
                        title={getStatusLabel(
                          phrase.status || PhraseStatus.UNREVIEWED
                        )}
                      >
                        <Circle
                          sx={{
                            fontSize: "0.75rem",
                            color:
                              phraseColorByStatus[phrase.status || "empty"],
                            cursor: "help",
                          }}
                        />
                      </Tooltip>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.7rem",
                            fontFamily: "monospace",
                            backgroundColor: "grey.100",
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            minWidth: "fit-content",
                          }}
                        >
                          {phrase.original_language || "??"}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ wordBreak: "break-word", flex: 1 }}
                        >
                          <FormattedText text={phrase.phrase} />
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {phrase.suitable_for_title && (
                        <Chip
                          label="Title"
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {phrase.suitable_for_description && (
                        <Chip
                          label="Description"
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      {phrase.suitable_for_bullet_points && (
                        <Chip
                          label="Bullets"
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {phrase.brand_ids && phrase.brand_ids.length > 0 ? (
                        phrase.brand_ids
                          .filter((brandId) =>
                            brands.find((b) => b.id === brandId)
                          )
                          .map((brandId) => (
                            <Chip
                              key={brandId}
                              label={getBrandName(brandId)}
                              size="small"
                              onClick={() => onBrandFilter(brandId)}
                              clickable
                              variant="outlined"
                              color="default"
                            />
                          ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No brands
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    {phrase.translation_status && (
                      <Tooltip
                        title={
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: "bold" }}
                            >
                              Translation Status:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, p: 0, pl: 2 }}>
                              {Object.entries(phrase.translation_status).map(
                                ([langCode, isAvailable]) => (
                                  <Typography
                                    component="li"
                                    variant="caption"
                                    key={langCode}
                                    sx={{
                                      color: isAvailable
                                        ? "#81c784"
                                        : "#ff8a65",
                                    }}
                                  >
                                    {languageCodeToLabel(
                                      langCode as LanguageCode
                                    )}
                                    : {isAvailable ? "Available" : "Missing"}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            backgroundColor: getTranslationColor(phrase),
                            color: "#000000",
                            fontSize: "0.75rem",
                            py: 0.3,
                            px: 0.7,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <GTranslate fontSize="inherit" sx={{ mr: 0.3 }} />
                          {`${getTranslationRatio(phrase).available}/${
                            getTranslationRatio(phrase).total
                          }`}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {phrase.usage_count && phrase.usage_count > 0 ? (
                        <Tooltip title="Click to view usage details">
                          <Chip
                            label={`${phrase.usage_count} uses`}
                            size="small"
                            color="info"
                            onClick={() => onShowUsage(phrase)}
                            clickable
                            icon={<Visibility />}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not used
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit phrase">
                        <IconButton
                          size="small"
                          onClick={() => onEditPhrase(phrase)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={
                          phrase.usage_count && phrase.usage_count > 0
                            ? "Cannot delete - phrase has usage"
                            : "Delete phrase"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDeletePhrase(phrase)}
                            color="error"
                            disabled={
                              !!(phrase.usage_count && phrase.usage_count > 0)
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PhrasebookTable;
